/**
 * FRONT-15: Onglet Bancaire V2 - Refonte complete
 * - Mode lecture par defaut, edition au clic "Modifier"
 * - Contacts comptabilite et facturation obligatoires (pas d'accordeon)
 * - Contact facturation = 1 seul champ email
 * - Validation telephone 10 chiffres
 * - IBAN exactement 27 caracteres (FR)
 * - Checkbox acceptation conditions Centralis
 */
import React, { useState, useEffect } from 'react';
import { useProfile } from '../../../contexts/ProfileContext';
import type { BancaireData, CoordonneesData } from '../../../services/profileService';
import Button from '../../../composants/Button/Button';
import './TabBancaire.css';

// Texte legal pour la checkbox d'acceptation des conditions
const CONDITIONS_TEXT = `Je reconnais être informé et accepter que Centralis se rémunère comme intermédiaire selon la modalité suivante : Facturation à l’entreprise prestataire, par la Centrale d’achat, de 5% du montant total HT des commandes reçues et acceptées par l’entreprise, par périodes trimestrielles (ou semestrielles si le volume de commandes reçues par l’entreprise est mieux adapté). La rémunération de Centralis est due dans tous les cas, à partir du moment où une commande a été diffusée et acceptée par le titulaire. Que le travail ait pu être mené ou qu’il ait été retardé ou même annulé par un acheteur, les modalités de clauses d’avances applicables ainsi que l’indemnité pour renonciation à faire exécuter une commande après validation constituent une sécurité suffisante pour honorer la rémunération de Centralis dans le délai contractuel. Le délai de paiement des factures de Centralis est de 30 jours calendaires. Passé ce délai, le prestataire sera redevable d’une indemnité forfaitaire de frais de recouvrement de 40 euros (article L 441-3 du Code de Commerce) ainsi que d’une pénalité de retard dont le taux est égal à 12%.`;

// Fonction pour formater l'IBAN (ajouter des espaces tous les 4 caracteres)
const formatIBAN = (iban: string): string => {
  const cleaned = iban.replace(/\s/g, '').toUpperCase();
  return cleaned.replace(/(.{4})/g, '$1 ').trim();
};

// FRONT-15: Validation IBAN francais - exactement 27 caracteres
const validateIBAN = (iban: string): { valid: boolean; message?: string } => {
  const cleaned = iban.replace(/\s/g, '').toUpperCase();

  if (!cleaned) {
    return { valid: false, message: 'L\'IBAN est requis' };
  }

  if (!cleaned.startsWith('FR')) {
    return { valid: false, message: 'L\'IBAN doit commencer par FR (France)' };
  }

  if (cleaned.length !== 27) {
    return { valid: false, message: 'L\'IBAN francais doit contenir exactement 27 caracteres' };
  }

  if (!/^FR[0-9]{2}[A-Z0-9]{23}$/.test(cleaned)) {
    return { valid: false, message: 'Format d\'IBAN invalide' };
  }

  return { valid: true };
};

// Fonction pour valider le BIC
const validateBIC = (bic: string): { valid: boolean; message?: string } => {
  const cleaned = bic.replace(/\s/g, '').toUpperCase();

  if (!cleaned) {
    return { valid: false, message: 'Le BIC est requis' };
  }

  if (cleaned.length !== 8 && cleaned.length !== 11) {
    return { valid: false, message: 'Le BIC doit contenir 8 ou 11 caracteres' };
  }

  if (!/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(cleaned)) {
    return { valid: false, message: 'Format de BIC invalide' };
  }

  return { valid: true };
};

// FRONT-20: Validation du numero de TVA intracommunautaire francais (OBLIGATOIRE)
const validateTVA = (tva: string): { valid: boolean; message?: string } => {
  // FRONT-20: Le champ devient obligatoire
  if (!tva || tva.trim() === '') {
    return {
      valid: false,
      message: 'Le numero de TVA intracommunautaire est obligatoire'
    };
  }

  const cleaned = tva.replace(/\s/g, '').toUpperCase();

  if (!/^FR[0-9A-Z]{2}[0-9]{9}$/.test(cleaned)) {
    return {
      valid: false,
      message: 'Format TVA invalide (ex: FR12345678901)'
    };
  }

  return { valid: true };
};

// FRONT-15: Validation telephone - exactement 10 chiffres
const validatePhone = (phone: string): { valid: boolean; message?: string } => {
  const cleaned = phone.replace(/\D/g, '');

  if (!cleaned) {
    return { valid: false, message: 'Le téléphone est requis' };
  }

  if (cleaned.length !== 10) {
    return { valid: false, message: 'Le téléphone doit contenir exactement 10 chiffres' };
  }

  return { valid: true };
};

// Formater le telephone pour l'affichage (XX XX XX XX XX)
const formatPhone = (phone: string | null | undefined): string => {
  if (!phone) return '-';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length !== 10) return phone;
  return cleaned.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
};

const TabBancaire: React.FC = () => {
  const { profile, updateBancaire, updateCoordonnees, isSaving } = useProfile();

  const [isEditing, setIsEditing] = useState(false);

  // Donnees bancaires avec TVA
  const [bancaireData, setBancaireData] = useState<BancaireData & { numero_tva?: string }>({
    nom_titulaire: '',
    banque: '',
    IBAN: '',
    BIC: '',
    numero_tva: '',
  });

  // FRONT-15: Donnees coordonnees avec acceptation conditions
  const [coordonneesData, setCoordonneesData] = useState<CoordonneesData>({
    legal_nom: '',
    legal_prenom: '',
    legal_fonction: '',
    legal_email: '',
    legal_tel: '',
    compta_nom: '',
    compta_prenom: '',
    compta_fonction: '',
    compta_email: '',
    compta_tel: '',
    facture_email: '',
    acceptation_conditions_centralis: false,
  });

  // Erreurs de validation
  const [errors, setErrors] = useState<{
    IBAN?: string;
    BIC?: string;
    numero_tva?: string;
    legal_tel?: string;
    compta_tel?: string;
    acceptation?: string;
  }>({});

  // Charger les donnees du profil
  // BUGFIX: Utiliser l'IBAN complet du profil pour le mode edition
  useEffect(() => {
    if (profile?.bancaire) {
      setBancaireData({
        nom_titulaire: profile.bancaire.nom_titulaire || '',
        banque: profile.bancaire.banque || '',
        IBAN: profile.bancaire.IBAN || '',  // FRONT-15: IBAN complet pour mode edition
        BIC: profile.bancaire.BIC || '',
        numero_tva: profile.bancaire.numero_tva || '',
      });
    }

    if (profile?.coordonnees) {
      const c = profile.coordonnees;
      setCoordonneesData({
        legal_nom: c.legal_nom || '',
        legal_prenom: c.legal_prenom || '',
        legal_fonction: c.legal_fonction || '',
        legal_email: c.legal_email || '',
        legal_tel: c.legal_tel || '',
        compta_nom: c.compta_nom || '',
        compta_prenom: c.compta_prenom || '',
        compta_fonction: c.compta_fonction || '',
        compta_email: c.compta_email || '',
        compta_tel: c.compta_tel || '',
        facture_email: c.facture_email || '',
        acceptation_conditions_centralis: c.acceptation_conditions_centralis || false,
      });
    }
  }, [profile]);

  // Gestion du changement d'IBAN
  const handleIBANChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '').toUpperCase();
    setBancaireData(prev => ({ ...prev, IBAN: value }));

    if (value.length > 0) {
      const validation = validateIBAN(value);
      setErrors(prev => ({ ...prev, IBAN: validation.valid ? undefined : validation.message }));
    } else {
      setErrors(prev => ({ ...prev, IBAN: undefined }));
    }
  };

  // Gestion du changement de BIC
  const handleBICChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '').toUpperCase();
    setBancaireData(prev => ({ ...prev, BIC: value }));

    if (value.length > 0) {
      const validation = validateBIC(value);
      setErrors(prev => ({ ...prev, BIC: validation.valid ? undefined : validation.message }));
    } else {
      setErrors(prev => ({ ...prev, BIC: undefined }));
    }
  };

  // Gestion du changement de TVA
  const handleTVAChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '').toUpperCase();
    setBancaireData(prev => ({ ...prev, numero_tva: value }));

    if (value.length > 0) {
      const validation = validateTVA(value);
      setErrors(prev => ({ ...prev, numero_tva: validation.valid ? undefined : validation.message }));
    } else {
      setErrors(prev => ({ ...prev, numero_tva: undefined }));
    }
  };

  // FRONT-20: Validation au blur pour TVA
  const handleTVABlur = () => {
    const validation = validateTVA(bancaireData.numero_tva || '');
    setErrors(prev => ({
      ...prev,
      numero_tva: validation.valid ? undefined : validation.message
    }));
  };

  // Gestion des autres champs bancaires
  const handleBancaireChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBancaireData(prev => ({ ...prev, [name]: value }));
  };

  // FRONT-15: Gestion des telephones avec validation 10 chiffres
  const handlePhoneChange = (field: 'legal_tel' | 'compta_tel') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setCoordonneesData(prev => ({ ...prev, [field]: value }));

    if (value.length > 0) {
      const validation = validatePhone(value);
      setErrors(prev => ({ ...prev, [field]: validation.valid ? undefined : validation.message }));
    } else {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Gestion des champs coordonnees
  const handleCoordonneesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCoordonneesData(prev => ({ ...prev, [name]: value }));
  };

  // FRONT-15: Gestion de la checkbox d'acceptation
  const handleAcceptationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setCoordonneesData(prev => ({ ...prev, acceptation_conditions_centralis: checked }));
    if (checked) {
      setErrors(prev => ({ ...prev, acceptation: undefined }));
    }
  };

  // FRONT-15: Soumission du formulaire (2 appels API)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Valider tous les champs
    const ibanValidation = validateIBAN(bancaireData.IBAN);
    const bicValidation = validateBIC(bancaireData.BIC);
    const tvaValidation = validateTVA(bancaireData.numero_tva || '');
    const legalTelValidation = validatePhone(coordonneesData.legal_tel || '');
    const comptaTelValidation = validatePhone(coordonneesData.compta_tel || '');

    const newErrors: typeof errors = {};

    if (!ibanValidation.valid) newErrors.IBAN = ibanValidation.message;
    if (!bicValidation.valid) newErrors.BIC = bicValidation.message;
    if (!tvaValidation.valid) newErrors.numero_tva = tvaValidation.message;
    if (!legalTelValidation.valid) newErrors.legal_tel = legalTelValidation.message;
    if (!comptaTelValidation.valid) newErrors.compta_tel = comptaTelValidation.message;
    if (!coordonneesData.acceptation_conditions_centralis) {
      newErrors.acceptation = 'Vous devez accepter les conditions de rémuneration Centralis';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Preparer les donnees coordonnees
    const coordToSend: CoordonneesData = {
      legal_nom: coordonneesData.legal_nom,
      legal_prenom: coordonneesData.legal_prenom,
      legal_fonction: coordonneesData.legal_fonction,
      legal_email: coordonneesData.legal_email,
      legal_tel: coordonneesData.legal_tel,
      compta_nom: coordonneesData.compta_nom,
      compta_prenom: coordonneesData.compta_prenom,
      compta_fonction: coordonneesData.compta_fonction,
      compta_email: coordonneesData.compta_email,
      compta_tel: coordonneesData.compta_tel,
      facture_email: coordonneesData.facture_email,
      acceptation_conditions_centralis: coordonneesData.acceptation_conditions_centralis,
    };

    // Appeler les 2 endpoints en parallele
    const [coordSuccess, bancSuccess] = await Promise.all([
      updateCoordonnees(coordToSend),
      updateBancaire(bancaireData),
    ]);

    if (coordSuccess && bancSuccess) {
      setIsEditing(false);
    }
  };

  // Annuler les modifications
  const handleCancel = () => {
    if (profile?.bancaire) {
      setBancaireData({
        nom_titulaire: profile.bancaire.nom_titulaire || '',
        banque: profile.bancaire.banque || '',
        IBAN: '',
        BIC: profile.bancaire.BIC || '',
        numero_tva: (profile.bancaire as any).numero_tva || '',
      });
    }

    if (profile?.coordonnees) {
      const c = profile.coordonnees;
      setCoordonneesData({
        legal_nom: c.legal_nom || '',
        legal_prenom: c.legal_prenom || '',
        legal_fonction: c.legal_fonction || '',
        legal_email: c.legal_email || '',
        legal_tel: c.legal_tel || '',
        compta_nom: c.compta_nom || '',
        compta_prenom: c.compta_prenom || '',
        compta_fonction: c.compta_fonction || '',
        compta_email: c.compta_email || '',
        compta_tel: c.compta_tel || '',
        facture_email: c.facture_email || '',
        acceptation_conditions_centralis: (c as any).acceptation_conditions_centralis || false,
      });
    }

    setErrors({});
    setIsEditing(false);
  };

  // Formater un contact pour l'affichage
  const formatContact = (prenom: string | null | undefined, nom: string | null | undefined): string => {
    const parts = [prenom, nom].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : '-';
  };

  // Verifier si les sections sont completes pour les pastilles
  const isLegalComplete = !!(
    profile?.coordonnees?.legal_nom &&
    profile?.coordonnees?.legal_prenom &&
    profile?.coordonnees?.legal_email &&
    profile?.coordonnees?.legal_tel
  );

  const isComptaComplete = !!(
    profile?.coordonnees?.compta_nom &&
    profile?.coordonnees?.compta_prenom &&
    profile?.coordonnees?.compta_email &&
    profile?.coordonnees?.compta_tel
  );

  const isFactureComplete = !!profile?.coordonnees?.facture_email;
  const isConditionsAccepted = !!(profile?.coordonnees as any)?.acceptation_conditions_centralis;

  const hasBancaireData = profile?.bancaire?.masked_iban && profile?.bancaire?.BIC;

  // Formater la date d'acceptation
  const formatAcceptationDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // MODE LECTURE
  if (!isEditing) {
    return (
      <div className="tab-bancaire">
        <div className="tab-header">
          <h2>Informations bancaires</h2>
          <Button
            value="Modifier"
            variant="secondary"
            onClick={() => setIsEditing(true)}
          />
        </div>

        {/* BLOC 1: Coordonnees facturation */}
        <div className="form-section-block">
          <div className="section-banner">Coordonnées facturation</div>
          <div className="section-content">

            {/* Representant legal */}
            <div className="contact-section">
              <div className="contact-header">
                <h4>Representant légal</h4>
                {!isLegalComplete && <span className="required-badge" title="Section incomplete"></span>}
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <label>Nom complet</label>
                  <span>{formatContact(profile?.coordonnees?.legal_prenom, profile?.coordonnees?.legal_nom)}</span>
                </div>
                <div className="info-item">
                  <label>Fonction</label>
                  <span>{profile?.coordonnees?.legal_fonction || '-'}</span>
                </div>
                <div className="info-item">
                  <label>Email</label>
                  <span>{profile?.coordonnees?.legal_email || '-'}</span>
                </div>
                <div className="info-item">
                  <label>Téléphone</label>
                  <span>{formatPhone(profile?.coordonnees?.legal_tel)}</span>
                </div>
              </div>
            </div>

            {/* Contact comptabilite */}
            <div className="contact-section">
              <div className="contact-header">
                <h4>Contact comptabilité</h4>
                {!isComptaComplete && <span className="required-badge" title="Section obligatoire incomplete"></span>}
              </div>
              {isComptaComplete ? (
                <div className="info-grid">
                  <div className="info-item">
                    <label>Nom complet</label>
                    <span>{formatContact(profile?.coordonnees?.compta_prenom, profile?.coordonnees?.compta_nom)}</span>
                  </div>
                  <div className="info-item">
                    <label>Fonction</label>
                    <span>{profile?.coordonnees?.compta_fonction || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Email</label>
                    <span>{profile?.coordonnees?.compta_email || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Téléphone</label>
                    <span>{formatPhone(profile?.coordonnees?.compta_tel)}</span>
                  </div>
                </div>
              ) : (
                <p className="empty-section-text">Non renseigné - Cliquez sur Modifier pour compléter</p>
              )}
            </div>

            {/* Contact facturation - email seul + checkbox conditions */}
            <div className="contact-section">
              <div className="contact-header">
                <h4>Adresse email de diffusion de facture de rémuneration Centralis</h4>
                {(!isFactureComplete || !isConditionsAccepted) && <span className="required-badge" title="Champ obligatoire"></span>}
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <label>Email</label>
                  <span>{profile?.coordonnees?.facture_email || '-'}</span>
                </div>
              </div>
              {/* Checkbox conditions inline */}
              <div className="conditions-inline">
                {isConditionsAccepted ? (
                  <p className="conditions-accepted">
                    Conditions acceptées le {formatAcceptationDate((profile?.coordonnees as any)?.acceptation_conditions_date)}
                  </p>
                ) : (
                  <p className="conditions-not-accepted">
                    <span className="required-badge"></span>
                    Conditions non acceptées - Cliquez sur Modifier pour accepter
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* BLOC 2: Coordonnees bancaires */}
        <div className="form-section-block">
          <div className="section-banner">Coordonnées bancaires</div>
          <div className="section-content">
            {hasBancaireData ? (
              <div className="info-grid">
                <div className="info-item">
                  <label>Titulaire du compte</label>
                  <span>{profile?.bancaire?.nom_titulaire || '-'}</span>
                </div>
                <div className="info-item">
                  <label>Banque</label>
                  <span>{profile?.bancaire?.banque || '-'}</span>
                </div>
                <div className="info-item">
                  <label>IBAN</label>
                  <span className="iban-value">{profile?.bancaire?.masked_iban || '-'}</span>
                </div>
                <div className="info-item">
                  <label>BIC / SWIFT</label>
                  <span>{profile?.bancaire?.BIC || '-'}</span>
                </div>
                <div className="info-item">
                  <label>N. TVA intracommunautaire</label>
                  <span>
                    {(profile?.bancaire as any)?.numero_tva || (
                      <span className="warning-text">Non renseignée</span>
                    )}
                  </span>
                </div>
              </div>
            ) : (
              <p className="empty-section-text">Aucune coordonnée bancaire renseignée</p>
            )}
          </div>
        </div>

      </div>
    );
  }

  // MODE EDITION
  return (
    <div className="tab-bancaire">
      <div className="tab-header">
        <h2>Modifier les coordonnées de facturation et bancaires</h2>
      </div>

      <form onSubmit={handleSubmit} className="bancaire-form">

        {/* BLOC 1: Coordonnees facturation */}
        <div className="form-section-block">
          <div className="section-banner">Coordonnées facturation</div>
          <div className="section-content">

            {/* Representant legal */}
            <div className="form-subsection">
              <h4>Representant légal *</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="legal_prenom">Prénom *</label>
                  <input
                    type="text"
                    id="legal_prenom"
                    name="legal_prenom"
                    value={coordonneesData.legal_prenom}
                    onChange={handleCoordonneesChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="legal_nom">Nom *</label>
                  <input
                    type="text"
                    id="legal_nom"
                    name="legal_nom"
                    value={coordonneesData.legal_nom}
                    onChange={handleCoordonneesChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="legal_fonction">Fonction</label>
                  <input
                    type="text"
                    id="legal_fonction"
                    name="legal_fonction"
                    value={coordonneesData.legal_fonction || ''}
                    onChange={handleCoordonneesChange}
                    placeholder="Ex: Gerant, President..."
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="legal_email">Email *</label>
                  <input
                    type="email"
                    id="legal_email"
                    name="legal_email"
                    value={coordonneesData.legal_email}
                    onChange={handleCoordonneesChange}
                    required
                  />
                </div>
                <div className={`form-group ${errors.legal_tel ? 'has-error' : ''}`}>
                  <label htmlFor="legal_tel">Téléphone *</label>
                  <input
                    type="tel"
                    id="legal_tel"
                    value={coordonneesData.legal_tel}
                    onChange={handlePhoneChange('legal_tel')}
                    placeholder="0123456789"
                    maxLength={10}
                    required
                  />
                  {errors.legal_tel && <span className="error-message">{errors.legal_tel}</span>}
                  <span className="field-hint">10 chiffres requis</span>
                </div>
              </div>
            </div>

            {/* Contact comptabilite - OBLIGATOIRE (pas d'accordeon) */}
            <div className="form-subsection">
              <div className="subsection-header">
                <h4>Contact comptabilité *</h4>
                <span className="required-badge" title="Section obligatoire"></span>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="compta_prenom">Prénom *</label>
                  <input
                    type="text"
                    id="compta_prenom"
                    name="compta_prenom"
                    value={coordonneesData.compta_prenom || ''}
                    onChange={handleCoordonneesChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="compta_nom">Nom *</label>
                  <input
                    type="text"
                    id="compta_nom"
                    name="compta_nom"
                    value={coordonneesData.compta_nom || ''}
                    onChange={handleCoordonneesChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="compta_fonction">Fonction</label>
                  <input
                    type="text"
                    id="compta_fonction"
                    name="compta_fonction"
                    value={coordonneesData.compta_fonction || ''}
                    onChange={handleCoordonneesChange}
                    placeholder="Ex: Comptable, DAF..."
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="compta_email">Email *</label>
                  <input
                    type="email"
                    id="compta_email"
                    name="compta_email"
                    value={coordonneesData.compta_email || ''}
                    onChange={handleCoordonneesChange}
                    required
                  />
                </div>
                <div className={`form-group ${errors.compta_tel ? 'has-error' : ''}`}>
                  <label htmlFor="compta_tel">Téléphone *</label>
                  <input
                    type="tel"
                    id="compta_tel"
                    value={coordonneesData.compta_tel || ''}
                    onChange={handlePhoneChange('compta_tel')}
                    placeholder="0123456789"
                    maxLength={10}
                    required
                  />
                  {errors.compta_tel && <span className="error-message">{errors.compta_tel}</span>}
                  <span className="field-hint">10 chiffres requis</span>
                </div>
              </div>
            </div>

            {/* Contact facturation - email + checkbox conditions */}
            <div className="form-subsection">
              <div className="subsection-header">
                <h4>Adresse email de diffusion de facture de rémuneration Centralis *</h4>
                <span className="required-badge" title="Champ obligatoire"></span>
              </div>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label htmlFor="facture_email">Email *</label>
                  <input
                    type="email"
                    id="facture_email"
                    name="facture_email"
                    value={coordonneesData.facture_email || ''}
                    onChange={handleCoordonneesChange}
                    placeholder="factures@entreprise.fr"
                    required
                  />
                </div>
              </div>
              {/* Checkbox acceptation conditions inline */}
              <div className={`checkbox-group-inline ${errors.acceptation ? 'has-error' : ''}`}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={coordonneesData.acceptation_conditions_centralis || false}
                    onChange={handleAcceptationChange}
                  />
                  <span className="checkbox-text">{CONDITIONS_TEXT}</span>
                </label>
                {errors.acceptation && <span className="error-message">{errors.acceptation}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* BLOC 2: Coordonnees bancaires */}
        <div className="form-section-block">
          <div className="section-banner">Coordonnées bancaires</div>
          <div className="section-content">
            <div className="form-intro">
              <p>
                Renseignez vos coordonnées bancaires pour recevoir les paiements.
                Ces informations sont chiffrées et sécurisées.
              </p>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="nom_titulaire">Nom du titulaire *</label>
                <input
                  type="text"
                  id="nom_titulaire"
                  name="nom_titulaire"
                  value={bancaireData.nom_titulaire}
                  onChange={handleBancaireChange}
                  placeholder="Nom du titulaire du compte"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="banque">Nom de la banque *</label>
                <input
                  type="text"
                  id="banque"
                  name="banque"
                  value={bancaireData.banque}
                  onChange={handleBancaireChange}
                  placeholder="Ex: BNP Paribas, Credit Agricole..."
                  required
                />
              </div>

              <div className={`form-group ${errors.IBAN ? 'has-error' : ''}`}>
                <label htmlFor="IBAN">IBAN *</label>
                <input
                  type="text"
                  id="IBAN"
                  value={formatIBAN(bancaireData.IBAN)}
                  onChange={handleIBANChange}
                  placeholder="FR76 1234 5678 9012 3456 7890 123"
                  maxLength={33}
                  required
                />
                {errors.IBAN && <span className="error-message">{errors.IBAN}</span>}
                <span className="field-hint">27 caracteres pour un IBAN francais</span>
              </div>

              <div className={`form-group ${errors.BIC ? 'has-error' : ''}`}>
                <label htmlFor="BIC">BIC / SWIFT *</label>
                <input
                  type="text"
                  id="BIC"
                  name="BIC"
                  value={bancaireData.BIC}
                  onChange={handleBICChange}
                  placeholder="BNPAFRPP"
                  maxLength={11}
                  required
                />
                {errors.BIC && <span className="error-message">{errors.BIC}</span>}
                <span className="field-hint">8 ou 11 caracteres</span>
              </div>

              <div className={`form-group ${errors.numero_tva ? 'has-error' : ''}`}>
                <label htmlFor="numero_tva">
                  N. TVA intracommunautaire *
                </label>
                <input
                  type="text"
                  id="numero_tva"
                  name="numero_tva"
                  value={bancaireData.numero_tva || ''}
                  onChange={handleTVAChange}
                  onBlur={handleTVABlur}
                  placeholder="FR12345678901"
                  maxLength={13}
                  required
                />
                {errors.numero_tva && <span className="error-message">{errors.numero_tva}</span>}
                <span className="field-hint">Format: FR + 11 caracteres</span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="form-actions">
          <Button
            value="Annuler"
            variant="secondary"
            onClick={handleCancel}
            disabled={isSaving}
          />
          <Button
            value={isSaving ? "Enregistrement..." : "Enregistrer"}
            variant="primary"
            type="submit"
            disabled={isSaving || !!errors.IBAN || !!errors.BIC || !!errors.numero_tva || !!errors.legal_tel || !!errors.compta_tel}
          />
        </div>
      </form>
    </div>
  );
};

export default TabBancaire;
